/**
 * Signal times: the customer's clock in, UTC to the bot, the customer's clock
 * back out.
 *
 * The bot's scheduler (frozen engine) reads the trailing /HH:MM of every signal
 * line as UTC. Customers type times in their own clock. The approved dashboard
 * (public/app.js, convertLocalSignalTimesToUtc) rewrote each line to UTC in the
 * browser right before posting, so a Haitian customer typing 10:30 got a trade
 * at 10:30 Haiti time. 4B-2 shipped without this step, so the raw text reached
 * the bot, "10:30" became 10:30 UTC — five hours earlier than meant, already in
 * the past — and 20 of a 23-line list were skipped while the three that
 * happened to still be in the future fired at the wrong wall-clock time. That
 * is the "bot assigns its own incorrect times" report, and it was ours.
 *
 * Ported logic-for-logic from the approved file. Do not "improve" the
 * conversion; the scheduler's expectations have not changed.
 */

/** Rewrite each line's trailing /HH:MM from the device's local clock to UTC. */
export function convertLocalSignalTimesToUtc(text: string): string {
  return text
    .split(/\r?\n/)
    .map((line) =>
      line.replace(/\/(\d{1,2}):(\d{2})\s*$/, (full: string, hh: string, mm: string) => {
        const h = Number(hh);
        const m = Number(mm);
        if (Number.isNaN(h) || Number.isNaN(m) || h > 23 || m > 59) return full;
        const d = new Date();
        d.setHours(h, m, 0, 0); // the customer's LOCAL clock, today
        // Already past today → tomorrow, so the bot does not skip it as stale.
        if (d.getTime() < Date.now()) d.setDate(d.getDate() + 1);
        const uh = String(d.getUTCHours()).padStart(2, '0');
        const um = String(d.getUTCMinutes()).padStart(2, '0');
        return `/${uh}:${um}`;
      })
    )
    .join('\n');
}

/** Local HH:MM from an absolute ms timestamp. */
export function localHHMM(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * Local HH:MM for a UTC HH:MM the bot holds (used for rows with no
 * scheduledFor, i.e. skipped at intake). Interpreted as today in UTC.
 */
export function localFromUtcHHMM(utcHour: number, utcMinute: number): string {
  const d = new Date();
  d.setUTCHours(utcHour, utcMinute, 0, 0);
  return localHHMM(d.getTime());
}

/** "America/Port-au-Prince · UTC-4" — what the upload hint shows. */
export function describeLocalTimezone(): string {
  let tz = '';
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    /* older WebViews */
  }
  const offsetMin = -new Date().getTimezoneOffset();
  const sign = offsetMin >= 0 ? '+' : '−';
  const abs = Math.abs(offsetMin);
  const oh = Math.floor(abs / 60);
  const om = abs % 60;
  const offsetStr = om === 0 ? `UTC${sign}${oh}` : `UTC${sign}${oh}:${String(om).padStart(2, '0')}`;
  return tz ? `${tz} · ${offsetStr}` : offsetStr;
}
